import { faker } from '@faker-js/faker';
import type { Asset, MaintenanceRecord, FailureCategory, MaintenanceType } from '@/types';

interface PredictedFailure {
  assetId: string;
  assetName: string;
  predictedDate: number;
  confidence: number;
  impact: number;
  category: FailureCategory;
}

interface SparePartForecast {
  partId: string;
  partName: string;
  stockLevel: number;
  recommendedLevel: number;
  predictedDemand: number;
  category: string;
  leadTime: number;
}

export function generateMaintenanceData() {
  // Generate assets
  const assets: Asset[] = Array.from({ length: 10 }, () => {
    const installDate = faker.date.past({ years: 5 });
    const lastMaintenance = faker.date.between({ 
      from: installDate, 
      to: new Date() 
    });
    const nextMaintenance = faker.date.future({ years: 1 });

    return {
      id: faker.string.uuid(),
      modelNumber: `MDL-${faker.string.numeric(5)}`,
      serialNumber: `SN-${faker.string.alphanumeric(8)}`,
      name: `${faker.commerce.productName()} Machine`,
      type: faker.helpers.arrayElement(['CNC', 'Assembly', 'Packaging', 'Testing', 'Welding']),
      manufacturer: faker.company.name(),
      installationDate: installDate,
      location: {
        site: faker.location.city(),
        area: faker.helpers.arrayElement(['Production', 'Assembly', 'QA', 'Packaging']),
        coordinates: [
          faker.number.float({ min: -90, max: 90 }), 
          faker.number.float({ min: -180, max: 180 })
        ]
      },
      status: faker.helpers.arrayElement(['operational', 'maintenance', 'repair', 'standby']),
      specifications: {
        dimensions: `${faker.number.int({ min: 100, max: 500 })}x${faker.number.int({ min: 100, max: 500 })}x${faker.number.int({ min: 100, max: 500 })} cm`,
        weight: faker.number.int({ min: 100, max: 5000 }),
        power: `${faker.number.int({ min: 1, max: 50 })} kW`,
        capacity: `${faker.number.int({ min: 100, max: 1000 })} units/hour`
      },
      lastMaintenanceDate: lastMaintenance,
      nextMaintenanceDate: nextMaintenance,
      operatingHours: faker.number.int({ min: 1000, max: 20000 })
    };
  });

  // Generate maintenance records
  const maintenanceRecords: MaintenanceRecord[] = assets.flatMap(asset => 
    Array.from({ length: faker.number.int({ min: 3, max: 8 }) }, () => {
      const startDate = faker.date.between({ 
        from: asset.installationDate, 
        to: new Date() 
      });
      const endDate = faker.date.between({
        from: startDate,
        to: new Date(startDate.getTime() + 24 * 60 * 60 * 1000)
      });

      return {
        id: faker.string.uuid(),
        assetId: asset.id,
        type: faker.helpers.arrayElement([
          'preventive',
          'corrective',
          'predictive',
          'upgrade',
          'calibration'
        ] as MaintenanceType[]),
        startDate,
        endDate,
        description: faker.lorem.paragraph(),
        technician: faker.person.fullName(),
        status: faker.helpers.arrayElement(['completed', 'scheduled', 'in_progress']),
        sparePartsUsed: Array.from(
          { length: faker.number.int({ min: 0, max: 3 }) },
          () => ({
            id: faker.string.uuid(),
            partNumber: `PART-${faker.string.numeric(5)}`,
            name: faker.commerce.productName(),
            quantity: faker.number.int({ min: 1, max: 5 }),
            unitCost: faker.number.float({ min: 50, max: 500 }),
            assetId: asset.id,
            maintenanceId: faker.string.uuid(),
            usageDate: startDate,
            category: faker.helpers.arrayElement([
              'machinery_parts',
              'custom_components',
              'raw_materials',
              'industrial_supplies',
              'maintenance_services'
            ])
          })
        ),
        findings: faker.helpers.maybe(() => faker.lorem.paragraph(), { probability: 0.7 }),
        recommendations: faker.helpers.maybe(() => faker.lorem.paragraph(), { probability: 0.5 }),
        failureCategory: faker.helpers.maybe(() => 
          faker.helpers.arrayElement([
            'mechanical',
            'electrical',
            'hydraulic',
            'pneumatic',
            'software',
            'structural',
            'wear',
            'operator_error',
            'environmental'
          ] as FailureCategory[]),
          { probability: 0.3 }
        ),
        failureDescription: faker.helpers.maybe(() => faker.lorem.paragraph(), { probability: 0.3 }),
        downtime: faker.number.float({ min: 1, max: 48, precision: 0.1 }),
        cost: faker.number.float({ min: 500, max: 5000 })
      };
    })
  );

  // Generate predicted failures for next 3 months
  const predictedFailures: PredictedFailure[] = assets.flatMap(asset => 
    Array.from({ length: faker.number.int({ min: 0, max: 2 }) }, () => ({
      assetId: asset.id,
      assetName: asset.name,
      predictedDate: faker.date.between({ 
        from: new Date(), 
        to: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000) 
      }).getTime(),
      confidence: faker.number.float({ min: 60, max: 95 }),
      impact: faker.number.float({ min: 1, max: 10 }),
      category: faker.helpers.arrayElement([
        'mechanical',
        'electrical',
        'hydraulic',
        'pneumatic',
        'software',
        'structural',
        'wear',
        'operator_error',
        'environmental'
      ] as FailureCategory[])
    }))
  );

  // Generate spare parts forecasts
  const sparePartsForecasts: SparePartForecast[] = Array.from({ length: 15 }, () => {
    const recommendedLevel = faker.number.int({ min: 20, max: 100 });
    return {
      partId: faker.string.uuid(),
      partName: faker.commerce.productName(),
      stockLevel: faker.number.int({ min: 5, max: recommendedLevel }),
      recommendedLevel,
      predictedDemand: faker.number.int({ min: 10, max: recommendedLevel * 1.2 }),
      category: faker.helpers.arrayElement([
        'Mechanical Components',
        'Electrical Parts',
        'Hydraulic Components',
        'Consumables',
        'Tools'
      ]),
      leadTime: faker.number.int({ min: 1, max: 30 })
    };
  });

  return {
    assets,
    maintenanceRecords,
    predictedFailures,
    sparePartsForecasts
  };
} 